import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import ConsentCheckbox, { RegisterConsentText } from "@/components/ConsentCheckbox";
import { track } from "@/lib/analytics";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setError("Debes aceptar los Términos y la Política de Privacidad para crear tu cuenta (Ley 21.719)");
      return;
    }
    setError(""); setLoading(true);
    track("sign_up_started", { from: params.get("from") || "direct" });
    const res = await register(email, password, nombre);
    setLoading(false);
    if (!res.ok) { setError(res.error); track("sign_up_failed", { reason: (res.error || "").slice(0, 60) }); return; }
    track("sign_up", { method: "email" });
    toast.success("Cuenta creada. Bienvenido a borra-me.com.");
    navigate(`/onboarding${planParam ? `?plan=${planParam}` : ""}`);
  };

  return (
    <div className="bg-[#F9F9F8] min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="eyebrow mb-3">Crear cuenta</div>
          <h1 className="font-display text-3xl font-bold text-gray-900 leading-tight">Empieza a proteger tus datos.</h1>
          <p className="mt-2 text-sm text-gray-600">¿Ya tienes cuenta? <Link to="/login" data-testid="register-link-login" className="underline">Ingresar</Link></p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="register-form">
            <div>
              <label className="eyebrow block mb-2">Nombre</label>
              <input data-testid="register-nombre" required value={nombre} onChange={(e) => setNombre(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <input data-testid="register-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Contraseña (mínimo 6)</label>
              <input data-testid="register-password" required minLength={6} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 outline-none focus:ring-2 focus:ring-[#0B2447] focus:border-transparent" />
            </div>
            {error && <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="register-error">{error}</div>}
            <div className="border-t border-gray-200 pt-4">
              <ConsentCheckbox
                checked={consent}
                onChange={setConsent}
                testid="register-consent"
              >
                <RegisterConsentText />
              </ConsentCheckbox>
            </div>
            <button data-testid="register-submit" disabled={loading || !consent} type="submit"
              className="w-full bg-[#0B2447] hover:bg-[#071932] text-white font-semibold py-3 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? "Creando..." : "Crear cuenta"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          <p className="mt-6 text-xs text-gray-500">Tus datos están protegidos por la Ley 21.719. Revisa nuestra{" "}
            <Link to="/legal/privacidad" className="underline">Política de Privacidad</Link>.
          </p>
        </div>
      </div>
      <div className="hidden lg:block flex-1 bg-[#0A0A0A] relative">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="absolute inset-0 flex items-center justify-center px-12">
          <div className="text-white max-w-md">
            <div className="eyebrow text-gray-400 mb-3">Lo que obtienes</div>
            <ul className="space-y-4 text-gray-200 mt-6">
              <li>· Escaneo en 14+ brokers chilenos</li>
              <li>· Solicitudes ARCOP enviadas automáticamente</li>
              <li>· Dashboard de privacidad en tiempo real</li>
              <li>· Escalada a la APDP con multas hasta 20.000 UTM</li>
              <li>· Re-escaneo automático cada 45 días</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
